import React from "react";

const SENTIMENT_STYLE = {
  positive:  { background: "#dcfce7", color: "#166534" },
  neutral:   { background: "#f1f5f9", color: "#475569" },
  negative:  { background: "#fee2e2", color: "#991b1b" },
  mixed:     { background: "#fef9c3", color: "#854d0e" },
};

const badge = (bg, color, text) => (
  <span style={{ background: bg, color, borderRadius: 6, padding: "2px 10px", fontSize: "0.75rem", fontWeight: 600, display: "inline-block" }}>
    {text}
  </span>
);

function SpeakerBubble({ turn }) {
  const isAgent = turn.speaker === "Agent";
  return (
    <div className={`d-flex mb-2 ${isAgent ? "justify-content-start" : "justify-content-end"}`}>
      <div
        style={{
          maxWidth: "75%",
          fontSize: "0.875rem",
          borderRadius: 12,
          padding: "8px 14px",
          background: isAgent ? "#ede9fe" : "#dbeafe",
          color: isAgent ? "#4c1d95" : "#1e3a5f",
        }}
      >
        <div style={{ fontSize: "0.7rem", fontWeight: 700, marginBottom: 3, opacity: 0.7 }}>
          {turn.speaker}
        </div>
        {turn.text}
      </div>
    </div>
  );
}

export default function CallAnalysisPanel({ loading, status, data }) {
  if (status === "processing" || (loading && !data)) {
    return (
      <div className="p-4 text-center" style={{ color: "#6b7280" }}>
        <div className="spinner-border spinner-border-sm me-2" />
        Analyzing call… this may take a few minutes.
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="p-4" style={{ color: "#dc2626" }}>
        Analysis failed: {data?.error || "Unknown error"}
      </div>
    );
  }

  if (!data || status !== "completed") return null;

  const sentStyle = SENTIMENT_STYLE[data.sentiment?.overall] || SENTIMENT_STYLE.neutral;
  const score = data.agentScore?.overall ?? 0;
  const scoreColor = score >= 7 ? "#166534" : score >= 4 ? "#854d0e" : "#991b1b";
  const scoreBg   = score >= 7 ? "#dcfce7" : score >= 4 ? "#fef9c3" : "#fee2e2";

  return (
    <div style={{ fontSize: "0.9rem" }}>
      {/* Summary */}
      <div className="mb-4">
        <div style={{ fontWeight: 700, marginBottom: 4, color: "#1e293b" }}>Summary</div>
        <p className="mb-0" style={{ color: "#475569", lineHeight: 1.6 }}>{data.summary}</p>
      </div>

      <div className="row g-3 mb-4">
        {/* Sentiment */}
        <div className="col-md-4">
          <div style={{ fontWeight: 700, marginBottom: 6, color: "#1e293b" }}>Sentiment</div>
          <div className="mb-2">
            {badge(sentStyle.background, sentStyle.color, data.sentiment?.overall)}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
            <div>Agent tone: {data.sentiment?.agentTone}</div>
            <div>Customer: {data.sentiment?.customerEmotion}</div>
          </div>
        </div>

        {/* Flags */}
        <div className="col-md-4">
          <div style={{ fontWeight: 700, marginBottom: 6, color: "#1e293b" }}>Flags</div>
          <div className="d-flex flex-wrap gap-2">
            {badge(
              data.issueResolved ? "#dcfce7" : "#fef9c3",
              data.issueResolved ? "#166534" : "#854d0e",
              data.issueResolved ? "Issue Resolved" : "Unresolved"
            )}
            {data.escalationFlag && badge("#fee2e2", "#991b1b", "Escalation Required")}
            {data.language && badge("#e0f2fe", "#0c4a6e", data.language)}
          </div>
        </div>

        {/* Tags */}
        <div className="col-md-4">
          <div style={{ fontWeight: 700, marginBottom: 6, color: "#1e293b" }}>Tags</div>
          <div className="d-flex flex-wrap gap-1">
            {(data.tags || []).map((tag) => (
              <span key={tag} style={{ background: "#f1f5f9", color: "#475569", borderRadius: 6, padding: "2px 8px", fontSize: "0.72rem", fontWeight: 600 }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Agent Score */}
      {data.agentScore && (
        <div className="mb-4">
          <div style={{ fontWeight: 700, marginBottom: 8, color: "#1e293b" }}>
            Agent Score:{" "}
            <span style={{ color: scoreColor, background: scoreBg, borderRadius: 6, padding: "1px 8px" }}>
              {data.agentScore.overall}/10
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {badge(
              data.agentScore.greetedProperly ? "#dcfce7" : "#fee2e2",
              data.agentScore.greetedProperly ? "#166534" : "#991b1b",
              `${data.agentScore.greetedProperly ? "✓" : "✗"} Greeted`
            )}
            {badge(
              data.agentScore.closedProperly ? "#dcfce7" : "#fee2e2",
              data.agentScore.closedProperly ? "#166534" : "#991b1b",
              `${data.agentScore.closedProperly ? "✓" : "✗"} Closed`
            )}
            {badge(
              !data.agentScore.interruptedCustomer ? "#dcfce7" : "#fef9c3",
              !data.agentScore.interruptedCustomer ? "#166534" : "#854d0e",
              data.agentScore.interruptedCustomer ? "⚠ Interrupted" : "✓ No Interruptions"
            )}
            {badge("#f1f5f9", "#475569", `Talk time: ${data.agentScore.talkTimeRatioPercent}%`)}
            {badge("#f1f5f9", "#475569", `Script: ${data.agentScore.scriptComplianceScore}/10`)}
          </div>
          {data.agentScore.notes && (
            <p style={{ color: "#64748b", fontSize: "0.8rem", marginBottom: 0 }}>
              💬 {data.agentScore.notes}
            </p>
          )}
        </div>
      )}

      {/* Action Items */}
      {data.actionItems?.length > 0 && (
        <div className="mb-4">
          <div style={{ fontWeight: 700, marginBottom: 6, color: "#1e293b" }}>Action Items</div>
          <ul className="mb-0 ps-3" style={{ fontSize: "0.875rem", color: "#475569" }}>
            {data.actionItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Conversation */}
      {data.conversation?.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 8, color: "#1e293b" }}>Conversation</div>
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: "12px",
              background: "#f8fafc",
              maxHeight: 300,
              overflowY: "auto",
            }}
          >
            {data.conversation.map((turn, i) => (
              <SpeakerBubble key={i} turn={turn} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
